/**
 * The date range selecting the issues that started inside it: a preset of days or months ending on
 * the last day of data (`?zoom=`, none for 12 months), or any start and end day (`?from=` / `?to=`).
 */
const PRESETS = [
    { key: "28d", label: "28 days", days: 28 },
    { key: "3m", label: "3 months", months: 3 },
    { key: "6m", label: "6 months", months: 6 },
    { key: "12m", label: "12 months", months: 12 },
];
export const DEFAULT_RANGE = "12m";

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const isDay = (value) => DAY.test(value || "") && !Number.isNaN(Date.parse(value));
const shift = (day, { months = 0, days = 0 }) => {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() - months);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};
const lengthOf = ({ from, to }) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;

/** The presets, each with its days: the last `days` days, or `months` months, up to `lastDay`. */
export const presetsEndingOn = (lastDay) => PRESETS.map(({ key, label, days, months }) => ({
    key, label, from: days ? shift(lastDay, { days: 1 - days }) : shift(lastDay, { months, days: 1 }), to: lastDay,
}));

/**
 * The range `params` select, with its key ("custom" for a range of days) and its label. A custom range
 * starting before `firstDay` starts on it and one ending after `lastDay` ends on it; one wholly outside
 * them is ignored.
 */
export const rangeOf = (params, lastDay, firstDay) => {
    const to = isDay(params.get("to")) && params.get("to") > lastDay ? lastDay : params.get("to");
    const from = isDay(params.get("from")) && params.get("from") < firstDay ? firstDay : params.get("from");
    const presets = presetsEndingOn(lastDay);
    const range = isDay(from) && isDay(to) && from <= to
        ? { key: "custom", from, to }
        : presets.find(({ key }) => key === params.get("zoom")) || presets.find(({ key }) => key === DEFAULT_RANGE);
    return { ...range, label: range.label || `${lengthOf(range)} days` };
};

export const isInside = ({ from, to }) => (day) => Boolean(day) && from <= day && day <= to;

const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const year = (iso) => iso.slice(0, 4);
const dayMonthYear = (iso) => `${dayMonth.format(new Date(iso))} ${year(iso)}`;

/** The range's filter chip label, "Dates: 28 days" or "Dates: 13 Feb – 19 May 2026"; null for the default 12 months, which is no filter. */
export const filterLabelOf = (range) => {
    if (range.key === DEFAULT_RANGE) return null;
    if (range.key !== "custom") return `Dates: ${range.label}`;
    const start = year(range.from) === year(range.to) ? dayMonth.format(new Date(range.from)) : dayMonthYear(range.from);
    return `Dates: ${start} – ${dayMonthYear(range.to)}`;
};

/** The later of the last crawl and the last day of traffic, so an issue the newest crawl found is inside every preset. */
export const lastDayOf = ({ generatedFrom, chart }) => [generatedFrom.crawl.day, chart?.end].filter(Boolean).sort().pop();

/** The earlier of the chart's first day and the first issue's start. */
export const firstDayOf = ({ chart, pages }) => [chart?.start, ...pages.flatMap((page) => page.issues.map((issue) => issue.since))].filter(Boolean).sort()[0];
